import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { Notice } from '@verlo/shared/components/ui/notice';

// The word a manager reads an alert by: its severity, as the language says it.
const SEVERITY = {
  critical: { tone: 'stop', word: 'Now' },
  warning:  { tone: 'warn', word: 'Watch' },
  info:     { tone: 'info', word: 'Note' },
};

const AlertsBanner = ({ site }) => {
  const [alerts, setAlerts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    reportsIntelApi
      .getAlerts(site)
      .then((res) => { if (!cancelled) { setAlerts(res.data.alerts || []); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [site]);

  if (!loaded) {
    return (
      <div className="mb-6 h-12 rounded-lg bg-paper/70 animate-pulse" data-testid="alerts-banner-loading" />
    );
  }

  if (alerts.length === 0) {
    return (
      <Notice tone="ok" word="Clear" className="mb-6" data-testid="alerts-banner-clear">
        No operational alerts for this site right now.
      </Notice>
    );
  }

  // The same alert twice is said once (Chris's review H5): a till that double-reports, or two
  // records of one cash-off, gave two identical cards.
  const shown = alerts.filter((a, i) => alerts.findIndex((b) => b.title === a.title && b.detail === a.detail) === i);

  return (
    <div className="mb-6 flex flex-col gap-2" data-testid="alerts-banner">
      {shown.map((a) => {
        const meta = SEVERITY[a.severity] || SEVERITY.info;
        return (
          <Notice
            key={a.id}
            tone={meta.tone}
            word={meta.word}
            title={a.title}
            data-testid={`alert-${a.id}`}
            action={a.link && (
              <Link
                to={a.link}
                className="text-[12px] font-semibold text-verlo-hover inline-flex items-center gap-0.5 hover:underline"
                data-testid={`alert-link-${a.id}`}
              >
                Investigate
                <ChevronRight size={12} />
              </Link>
            )}
          >
            {a.detail}
          </Notice>
        );
      })}
    </div>
  );
};

export default AlertsBanner;
